import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User } from 'lucide-react';
import {
  Plus,
  MoreVertical,
  TrendingUp,
  TrendingDown,
  Briefcase,
  CheckCircle,
  Clock,
  Calendar,
  Download,
  AlertCircle,
  XCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LabelList
} from 'recharts';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { exportToExcel, formatDataForExport } from '../utils/excelExport';
import dayjs from 'dayjs';

// Animated counter: counts from previous value to new value
function CountUp({ to = 0, duration = 700 }) {
  const [val, setVal] = useState(to);
  const prev = useRef(to);
  const raf = useRef(null);

  useEffect(() => {
    const from = prev.current;
    const diff = to - from;
    if (diff === 0) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + diff * eased));
      if (p < 1) { raf.current = requestAnimationFrame(tick); }
      else { prev.current = to; }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [to, duration]);

  return <>{val}</>;
}

const Dashboard = () => {
  const { token, user, isAdmin, isPM, isTL } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [taskStats, setTaskStats] = useState({});
  const [tasksByStatus, setTasksByStatus] = useState({});
  const [tasksByStage, setTasksByStage] = useState({});
  const [teamTaskStatus, setTeamTaskStatus] = useState(null);
  const [userTaskStatus, setUserTaskStatus] = useState(null);
  const [projectsByHours, setProjectsByHours] = useState({});
  const [completionRate, setCompletionRate] = useState(0);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientWorkflowData, setClientWorkflowData] = useState([]);
  const [projectsByStage, setProjectsByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  // Date range state
  const [dateRange, setDateRange] = useState('today'); // 'today', 'week', 'month', 'custom'
  const [customStart, setCustomStart] = useState(dayjs().format('YYYY-MM-DD'));
  const [customEnd, setCustomEnd] = useState(dayjs().format('YYYY-MM-DD'));

  // Active users (on shift)
  const activeUsers = users.filter(u => u.on_shift);

  // For team leads, restrict visible active users to their team members
  const getLeaderMemberRoles = (role) => {
    switch (role) {
      case 'prepress_lead':
        return ['prepress', 'prepress_lead', 'preflight_prepress'];
      case 'preflight_lead':
        return ['preflight', 'preflight_lead', 'preflight_prepress'];
      case 'cmd_lead':
        return ['cmd', 'cmd_lead'];
      case 'qc_lead':
        return ['qc', 'qc_lead'];
      default:
        return [];
    }
  };

  const visibleActiveUsers = (() => {
    if (isAdmin) return activeUsers;
    if (isTL && user?.role) {
      const memberRoles = getLeaderMemberRoles(user.role);
      if (!memberRoles.length) return activeUsers;
      return activeUsers.filter(u => memberRoles.includes(u.role));
    }
    return activeUsers;
  })();

  const getDateRange = () => {
    let startDate, endDate;
    const today = dayjs();

    if (dateRange === 'today') {
      startDate = today.startOf('day');
      endDate = today.endOf('day');
    } else if (dateRange === 'week') {
      startDate = today.startOf('week');
      endDate = today.endOf('week');
    } else if (dateRange === 'month') {
      startDate = today.startOf('month');
      endDate = today.endOf('month');
    } else if (dateRange === 'custom') {
      startDate = dayjs(customStart);
      endDate = dayjs(customEnd);
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };

  const handleExportDashboard = () => {
    // Export by stage/team
    const stages = ['preflight', 'cmd', 'prepress', 'qc', 'production'];
    
    stages.forEach(stage => {
      const stageProjects = projects.filter(p => p.current_stage === stage);
      const stageData = stageProjects.map(p => ({
        'Project': p.title || p.id,
        'Client': p.client,
        'Job Code': p.job_code || '-',
        'Status': p.current_status || 'not_started',
        'Priority': p.priority || 'Normal',
        'Stage': stage
      }));
      
      if (stageData.length > 0) {
        exportToExcel(stageData, `projects_${stage}_${new Date().getTime()}.csv`);
      }
    });

    const pausedStatuses = new Set(['paused', 'query', 'on_hold', 'hold']);

    // Export paused and rejected
    const pausedProjects = projects.filter(p => pausedStatuses.has(p.current_status));
    if (pausedProjects.length > 0) {
      const pausedData = pausedProjects.map(p => ({
        'Project': p.title || p.id,
        'Client': p.client,
        'Job Code': p.job_code || '-',
        'Status': p.current_status,
        'Priority': p.priority || 'Normal'
      }));
      exportToExcel(pausedData, `projects_paused_${new Date().getTime()}.csv`);
    }

    const rejectedProjects = projects.filter(p => p.current_status === 'rejected');
    if (rejectedProjects.length > 0) {
      const rejectedData = rejectedProjects.map(p => ({
        'Project': p.title || p.id,
        'Client': p.client,
        'Job Code': p.job_code || '-',
        'Status': p.current_status,
        'Priority': p.priority || 'Normal'
      }));
      exportToExcel(rejectedData, `projects_rejected_${new Date().getTime()}.csv`);
    }
  };

  const applyDashboardData = useCallback((dashboardData) => {
    setProjects(dashboardData.projects || []);
    setProjectsByStage(dashboardData.projectsByStage || {});
    setClientWorkflowData(dashboardData.clientWorkflow || []);
    setStats(dashboardData.stats || {});
    setTaskStats(dashboardData.taskStats || {});
    setTasksByStatus(dashboardData.tasksByStatus || {});
    setTasksByStage(dashboardData.tasksByStage || {});
    setTeamTaskStatus(dashboardData.teamTaskStatus || null);
    setUserTaskStatus(dashboardData.userTaskStatus || null);
    setProjectsByHours(dashboardData.projectsByHours || {});
    setCompletionRate(dashboardData.completionRate || 0);
  }, []);

  const loadDashboardData = useCallback(async () => {
    if (!token) { setLoading(false); return; }

    const cacheKey = `dashboard_cache_${user?.id || 'all'}_${dateRange}`;

    // Show cached data instantly so the page appears immediately
    let hasCachedData = false;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const { data } = JSON.parse(raw);
        applyDashboardData(data);
        setLoading(false);
        setRefreshing(true);
        hasCachedData = true;
      }
    } catch {}

    if (!hasCachedData) setLoading(true);

    try {
      const dashboardResponse = await api.get('/dashboard/projects', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const dashboardData = dashboardResponse.data;
      applyDashboardData(dashboardData);
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ data: dashboardData, ts: Date.now() }));
      } catch {}
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      if (!hasCachedData) setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, user?.id, dateRange, applyDashboardData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (!token) return;
    api.get('/admin/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(resp => setUsers(Array.isArray(resp.data) ? resp.data : []))
      .catch(() => setUsers([]));
  }, [token]);

  // Mock data for charts - now using client workflow data
  const lineChartData = clientWorkflowData.length > 0 ? clientWorkflowData : [
    { name: 'Client A', total: 40, completed: 24, in_progress: 10, paused: 6 },
    { name: 'Client B', total: 30, completed: 13, in_progress: 12, paused: 5 },
    { name: 'Client C', total: 20, completed: 18, in_progress: 1, paused: 1 },
    { name: 'Client D', total: 27, completed: 15, in_progress: 8, paused: 4 },
  ];
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard…</p>
          <p className="text-gray-400 text-sm mt-1">Fetching latest data</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Error Loading Dashboard</h3>
          <p className="text-gray-600 text-center">{error}</p>
        </div>
      </div>
    );
  }
  const getStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'review': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'planning': return 'bg-purple-100 text-purple-800';
      case 'on_hold': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'on_hold': return 'bg-red-100 text-red-700';
      case 'paused': return 'bg-blue-100 text-blue-700';
      case 'query': return 'bg-gray-200 text-gray-700';
      case 'approval': return 'bg-indigo-100 text-indigo-700';
      case 'unassigned': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            {refreshing && (
              <span className="flex items-center gap-1.5 text-xs text-blue-500 font-medium bg-blue-50 px-2.5 py-1 rounded-full">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse inline-block" />
                Refreshing…
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Export Button */}
          {/* <button
            onClick={handleExportDashboard}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Download className="w-5 h-5" />
            Export
          </button> */}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="space-y-6">
        {(isTL && teamTaskStatus) && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Team Tasks</h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="bg-gradient-to-br from-red-400 to-gray-300 rounded-xl p-4 text-center">
                <p className="text-s text-gray-800 mb-1">Unassigned</p>
                <p className="text-2xl font-bold text-gray-900"><CountUp to={teamTaskStatus.unassigned || 0} /></p>
              </div>
              <div className="bg-gradient-to-br from-indigo-300 to-gray-300 rounded-xl p-4 text-center">
                <p className="text-s text-gray-800 mb-1">Pending</p>
                <p className="text-2xl font-bold text-gray-900"><CountUp to={teamTaskStatus.pending || 0} /></p>
              </div>
              <div className="bg-gradient-to-br from-green-300 to-gray-300 rounded-xl p-4 text-center">
                <p className="text-s text-gray-800 mb-1">In Progress</p>
                <p className="text-2xl font-bold text-gray-900"><CountUp to={teamTaskStatus.in_progress || 0} /></p>
              </div>
              <div className="bg-gradient-to-br from-rose-300 to-gray-300 rounded-xl p-4 text-center">
                <p className="text-s text-gray-800 mb-1">On Hold</p>
                <p className="text-2xl font-bold text-gray-900"><CountUp to={teamTaskStatus.on_hold || 0} /></p>
              </div>
              <div className="bg-gradient-to-br from-yellow-400 to-gray-300 rounded-xl p-4 text-center">
                <p className="text-s text-gray-800 mb-1">Paused</p>
                <p className="text-2xl font-bold text-gray-900"><CountUp to={teamTaskStatus.paused || 0} /></p>
              </div>
              <div className="bg-gradient-to-br from-gray-300 to-gray-100 rounded-xl p-4 text-center">
                <p className="text-s text-gray-800 mb-1">Query</p>
                <p className="text-2xl font-bold text-gray-900"><CountUp to={teamTaskStatus.query || 0} /></p>
              </div>
            </div>
          </div>
        )}

     
        {(!isAdmin && !isPM) && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4">My Tasks</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-red-500 rounded-xl p-4 text-center">
                <p className="text-s text-red-50 mb-1">Pending</p>
                <p className="text-2xl font-bold text-red-50"><CountUp to={(userTaskStatus && userTaskStatus.pending) || 0} /></p>
              </div>
              <div className="bg-green-500 text-white rounded-xl p-4 text-center">
                <p className="text-s text-green-50 mb-1">In Progress</p>
                <p className="text-2xl font-bold text-green-50"><CountUp to={(userTaskStatus && userTaskStatus.in_progress) || 0} /></p>
              </div>
              <div className="bg-orange-500 text-white rounded-xl p-4 text-center">
                <p className="text-s text-orange-50 mb-1">On Hold</p>
                <p className="text-2xl font-bold text-orange-50"><CountUp to={(userTaskStatus && userTaskStatus.on_hold) || 0} /></p>
              </div>
              <div className="bg-blue-500 text-white rounded-xl p-4 text-center">
                <p className="text-s text-blue-50 mb-1">Paused</p>
                <p className="text-2xl font-bold text-blue-50"><CountUp to={(userTaskStatus && userTaskStatus.paused) || 0} /></p>
              </div>
              <div className="bg-rose-500 rounded-xl p-4 text-center">
                <p className="text-s text-rose-50 mb-1">Query</p>
                <p className="text-2xl font-bold text-rose-50"><CountUp to={(userTaskStatus && userTaskStatus.query) || 0} /></p>
              </div>
            </div>
          </div>
        )}

        {/* PM Overview - Only for Project Managers */}
        {isPM && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4">PM Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {/* Today Created */}
              <div className="bg-blue-600 text-white rounded-xl p-4 text-center">
                <p className="text-m text-blue-50 mb-1">Today Created</p>
                <p className="text-2xl font-bold text-blue-50"><CountUp to={stats.today_created || 0} /></p>
              </div>

              {/* Today Completed */}
              <div className="bg-sky-600 text-white rounded-xl p-4 text-center">
                <p className="text-m text-sky-50 mb-1">Today Completed</p>
                <p className="text-2xl font-bold text-sky-50"><CountUp to={stats.today_completed || 0} /></p>
              </div>

              {/* Pending */}
              <div className="bg-red-500 text-white rounded-xl p-4 text-center">
                <p className="text-m text-red-50 mb-1">Pending</p>
                <p className="text-2xl font-bold text-red-50"><CountUp to={(tasksByStatus && tasksByStatus.pending) || 0} /></p>
              </div>

              {/* In Progress */}
              <div className="bg-green-500 text-white rounded-xl p-4 text-center">
                <p className="text-m text-green-50 mb-1">In Progress</p>
                <p className="text-2xl font-bold text-green-50"><CountUp to={(tasksByStatus && tasksByStatus.in_progress) || 0} /></p>
              </div>

              {/* Approval */}
              <div className="bg-indigo-500 text-white rounded-xl p-4 text-center">
                <p className="text-m text-indigo-50 mb-1">Approval</p>
                <p className="text-2xl font-bold text-indigo-50"><CountUp to={(tasksByStatus && (tasksByStatus.approval || tasksByStatus.on_approval)) || 0} /></p>
              </div>

              {/* On Hold */}
              <div className="bg-rose-500 text-white rounded-xl p-4 text-center">
                <p className="text-m text-rose-50 mb-1">On Hold</p>
                <p className="text-2xl font-bold text-rose-50"><CountUp to={(tasksByStatus && (tasksByStatus.on_hold || tasksByStatus.hold)) || 0} /></p>
              </div>
            </div>
          </div>
        )}

        {/* Client Job Workflow Chart */}
        {/* <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Client Job Workflow</h2>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af' }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af' }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Line type="monotone" dataKey="total" stroke="#0ea5e9" strokeWidth={3} dot={{ r: 4, fill: '#0ea5e9', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} />
                <Line type="monotone" dataKey="in_progress" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div> */}

        {/* Summary Metrics Grid - Admin only */}
        {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-6">
          {/* Total Projects */}
          <div className="bg-indigo-800 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">Total Projects</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.total || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-blue-50 text-indigo-600 rounded-xl">
                <Briefcase className="w-6 h-6" />
              </div> */}
            </div>
          </div>

          {/* Today Created Projects */}
          <div className="bg-blue-600 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">Today Created</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.today_created || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <Briefcase className="w-6 h-6" />
              </div> */}
            </div>
          </div>

          {/* Today completed projects */}
          <div className="bg-sky-600 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">Today Completed</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.today_completed || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-green-50 text-green-600 rounded-xl">
                <CheckCircle className="w-6 h-6" />
              </div> */}
            </div>
          </div>

          {/* Unassigned Projects */}
          <div className="bg-red-600 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">Unassigned</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.unassigned || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-red-50 text-red-600 rounded-xl">
                <AlertCircle className="w-6 h-6" />
              </div> */}
            </div>
          </div>

          {/* Not Started Projects */}
          <div className="bg-orange-500 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">Not Started</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.not_started || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-purple-50 text-orange-600 rounded-xl">
                <Calendar className="w-6 h-6" />
              </div> */}
            </div>
          </div>

          {/* In Progress Projects */}
          <div className="bg-green-500 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">In Progress</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.in_progress || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-orange-50 text-green-600 rounded-xl">
                <Clock className="w-6 h-6" />
              </div> */}
            </div>
          </div>

          {/* Completed Projects */}
          {/* <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Completed</p>
                <h3 className="text-3xl font-bold text-gray-900">{stats.completed || 0}</h3>
              </div>
              <div className="p-3 bg-green-50 text-green-600 rounded-xl">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
          </div> */}

          {/* Paused Projects */}
          <div className="bg-rose-500 text-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2">Paused / Hold</p>
                <h3 className="text-3xl font-bold "><CountUp to={stats.paused || 0} /></h3>
              </div>
              {/* <div className="p-3 bg-yellow-50 text-cyan-500 rounded-xl">
                <AlertCircle className="w-6 h-6" />
              </div> */}
            </div>
          </div>
        </div>
        )}
  {/* Charts Section - 3 Column Layout (Admin only) */}
  {isAdmin && (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Tasks in Current Stage Section - Card Grid */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 md:col-span-2">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Tasks in Current Stage</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 text-gray-800">
              {/* Die Creation */}
              <div className="bg-gradient-to-br from-gray-300 to-gray-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Die Creation</p><br />
                  <h3 className="text-3xl font-bold">{tasksByStage.die_creation || 0}</h3>
                </div>
              </div>

              {/* Recreation */}
              <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Recreation</p><br />
                  <h3 className="text-3xl font-bold">{tasksByStage.recreation || 0}</h3>
                </div>
              </div>

              {/* Preflight */}
              <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Preflight</p><br />
                  <h3 className="text-3xl font-bold">{tasksByStage.preflight || 0}</h3>
                </div>
              </div>

              {/* Single-up Approval */}
              <div className="bg-gradient-to-br from-pink-300 to-pink-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Single-up Approval</p>
                  <h3 className="text-3xl font-bold">{tasksByStage['single-up_approval'] || 0}</h3>
                </div>
              </div>

              {/* CMD */}
              <div className="bg-gradient-to-br from-orange-300 to-orange-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">CMD</p><br />
                  <h3 className="text-3xl font-bold">{tasksByStage.cmd || 0}</h3>
                </div>
              </div>

              {/* Prepress QC (Self QC) */}
              <div className="bg-gradient-to-br from-cyan-300 to-cyan-300 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Prepress QC</p><br />
                  <h3 className="text-3xl font-bold">{tasksByStage.prepress_qc_self_qc || 0}</h3>
                </div>
              </div>

              {/* Layout Approval */}
              <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Layout Approval</p>
                  <h3 className="text-3xl font-bold">{tasksByStage.layout_approval || 0}</h3>
                </div>
              </div>

              {/* RIP QC */}
              <div className="bg-gradient-to-br from-indigo-300 to-indigo-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">RIP QC</p><br />
                  <h3 className="text-3xl font-bold">{tasksByStage.rip_qc || 0}</h3>
                </div>
              </div>

              {/* Epson Printout */}
              <div className="bg-gradient-to-br from-green-300 to-green-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Epson Printout</p>
                  <h3 className="text-3xl font-bold">{tasksByStage.epson_printout || 0}</h3>
                </div>
              </div>

              {/* Plate & Despatch */}
              <div className="bg-gradient-to-br from-gray-300 to-gray-400 rounded-xl p-4">
                <div className="text-center">
                  <p className="text-s font-medium opacity-90 mb-1">Plate & Despatch</p>
                  <h3 className="text-3xl font-bold">{tasksByStage.plate_and_despatch || 0}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks by Hours (Project Age) Section - Column Chart */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 md:col-span-2">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Projects by Age</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Less than 4 hrs', projects: projectsByHours.less_than_4hrs || 0 },
                  { name: '4 - 8 hrs', projects: projectsByHours.more_than_4hrs || 0 },
                  { name: '8 - 12 hrs', projects: projectsByHours.more_than_8hrs || 0 },
                  { name: '12 - 24 hrs', projects: projectsByHours.more_than_12hrs || 0 },
                  { name: '24 - 48 hrs', projects: projectsByHours.more_than_24hrs_less_than_48hrs || 0 },
                  { name: '48 hrs +', projects: projectsByHours.more_than_48hrs || 0 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                  <Bar dataKey="projects" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                    <LabelList dataKey="projects" position="top" fill="#111827" fontSize={12} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        )}

        {(isAdmin || isTL) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Active Users - Admin only */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <User className="h-4 w-4 text-green-600" />
                Active Users
              </h2>
              <span className="text-xs text-gray-500">On shift</span>
            </div>
            <div className="text-3xl font-bold text-green-600 mb-3">
              {visibleActiveUsers.length}
            </div>
            <div className="mt-auto max-h-32 overflow-y-auto border-t border-gray-100 pt-2">
              {visibleActiveUsers.length === 0 ? (
                <p className="text-xs text-gray-500">No active users currently on shift.</p>
              ) : (
                <ul className="text-xs text-gray-700 space-y-1">
                  {visibleActiveUsers.map(u => (
                    <li key={u.id} className="flex justify-between">
                      <span>{u.name}</span>
                      <span className="text-gray-400">{u.role}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>  
        </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
